import { Platform, Pressable, StyleSheet, View, useWindowDimensions } from "react-native";
import { useTheme } from "../../../../hooks/use-theme";
import LogoImage from "../../../../assets/images/logo.svg";
import VStack from "../../../../ui_atoms/VStack";
import { getAuth, RecaptchaVerifier } from "../../../../contexts/FirebaseAuth";
import QRCode from "react-qr-code";
import Text from "../../../../components/Text";
import MarkdownView from "../../../../components/MarkdownView";
import { useCallback, use, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useI18n } from "../../../../hooks/i18n-hooks";
import Constants from "expo-constants";
import Button from "../../../../components/Button";
import PhoneField, { PhoneFieldHandle } from "../../../../components/PhoneField";
import { WelcomeScreenProps } from "../Welcome";
import { AuthManagerContext } from "../../../../contexts/AuthManager";
import commonStyle from "../../../common-style";
import CodeField from "../../../../components/CodeField";
import { CELL_COUNT } from "../../ConfirmationCode";
import Loading from "../../../../components/Loading";
import { ConfirmationResult } from "@react-native-firebase/auth";
import { FirebaseManagerContext } from "../../../../contexts/FirebaseManager";
import { RandomLine, RandomLineFunctions } from "../../../../components/RandomLine";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BlurView from "../../../../components/BlurView";
import { LinearGradient } from "expo-linear-gradient";
import {
    BodyMediumText,
    BodySmallText,
    TitleLargeText,
} from "../../../../components/FormattedText";
import AutoSizeImage from "../../../../components/AutoSizeImage";
import MaskedView from "@react-native-masked-view/masked-view";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { LanguageSelector } from "./LanguageSelector";
import { useSetAtom } from "jotai";
import { invitationCodeStepDone, pendingInvitationCode } from "../../../../atoms/auth";

const AnimatedMarkdownView = Animated.createAnimatedComponent(MarkdownView);

const PARTNER_LOGO_HEIGHT = 28;

const LARGE_CAROUSEL_BORDER_WIDTH = 10;
const LARGE_CAROUSEL_WIDTH = 350;
const LARGE_CAROUSEL_HEIGHT = 500;
const BUTTON_BORDER_RADIUS = 47;

type MessageInfo = {
    text: string;
    color?: string;
};

const captchaDisabledPhones = (
    (Constants.expoConfig?.extra?.captchaDisabledPhones as string) || ""
).split(",");

const CAPTCHA_ID = uuidv4();

export const LargeLayout = ({ navigation, route }: WelcomeScreenProps) => {
    const { isLogin: _isInitialLogin = "false" } = route.params || {};
    const isInitialLogin = _isInitialLogin.toLowerCase() === "true";
    const [isLogin, setIsLogin] = useState(isInitialLogin);
    // Read the route param (given by the invitation link screen), else the URL search param, once
    // at mount. Computing this lazily inside `useState` keeps render pure (no ref writes / side
    // effects in render).
    const [invitedByParam] = useState<string | null>(() => {
        if (route.params?.invited_by) return route.params.invited_by;
        if (Platform.OS !== "web") return null;
        const url = new URL(window.location.href);
        return url.searchParams.get("invited_by");
    });
    const [invitationCodeParam] = useState<string | null>(() => {
        if (route.params?.invitation_code) return route.params.invitation_code;
        if (Platform.OS !== "web") return null;
        const url = new URL(window.location.href);
        return url.searchParams.get("invitation_code");
    });
    const setPendingInvitationCode = useSetAtom(pendingInvitationCode);
    const setInvitationCodeStepDone = useSetAtom(invitationCodeStepDone);
    useEffect(() => {
        if (invitationCodeParam) {
            // An invitation sent by email: the signup asks for its code, prefilled with this one.
            setPendingInvitationCode(invitationCodeParam);
            setInvitationCodeStepDone(false);
        }
    }, [invitationCodeParam, setInvitationCodeStepDone, setPendingInvitationCode]);
    const styles = useStyles();
    const { colors, gridSize } = useTheme();
    const i18n = useI18n();
    const phoneInputRef = useRef<PhoneFieldHandle>(null);
    const [message, setMessage] = useState<MessageInfo>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [codeValue, setCodeValue] = useState("");
    const [phoneNumberConfirmResultValue, setPhoneNumberConfirmResult] =
        useState<ConfirmationResult>();
    const { user, signInWithPhoneNumber } = use(AuthManagerContext);
    const { user: firebaseUser } = use(FirebaseManagerContext);

    const invitedByLabel = useMemo(() => {
        if (!invitedByParam) return null;
        return invitedByParam === "unknown"
            ? i18n.t("screens.auth.welcome.a_personn")
            : invitedByParam;
    }, [invitedByParam, i18n]);

    const textType = useMemo(() => {
        if (isLogin) {
            return "login";
        }
        if (invitedByLabel) {
            return "invitation";
        }
        return "signup";
    }, [isLogin, invitedByLabel]);

    // Phone number part

    const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
    useEffect(() => {
        setIsPhoneNumberValid(
            (phoneNumber?.length || 0) > 0 && !!phoneInputRef.current?.isValidNumber(phoneNumber),
        );
    }, [phoneNumber]);

    const validatePhoneNumber = useCallback(async () => {
        try {
            setIsProcessing(true);
            const phoneValid = phoneInputRef.current?.isValidNumber(phoneNumber);
            if (!phoneValid) {
                setMessage({
                    text: i18n.t("screens.auth.phone_number.invalid_number"),
                    color: "red",
                });
                return;
            }

            const formattedPhoneNumber = phoneInputRef.current?.formatPhoneNumber();

            if (formattedPhoneNumber) {
                if (Platform.OS === "web") {
                    if (captchaDisabledPhones.includes(formattedPhoneNumber)) {
                        getAuth().settings.appVerificationDisabledForTesting = true;
                    }

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (!window.verifier) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        window.verifier = new RecaptchaVerifier(getAuth(), CAPTCHA_ID, {
                            size: "invisible",
                        });
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    window.verifier.verify();
                }

                const confirmation = await signInWithPhoneNumber(phoneNumber);
                setPhoneNumberConfirmResult(confirmation);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                if (window.verifier) {
                    try {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                        window.verifier.clear();
                    } catch {
                        // Ignore cleanup errors
                    }
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    window.verifier = null;
                }
            }
        } catch (err) {
            let errorMessage: string;
            if (err instanceof Error) {
                errorMessage = err.message;
            } else {
                errorMessage = String(err);
            }
            setMessage({ text: `Error: ${errorMessage}`, color: "red" });
        } finally {
            setIsProcessing(false);
        }
    }, [phoneNumber, i18n, signInWithPhoneNumber]);

    const onPhoneNumberChanged = useCallback((text: string) => {
        setPhoneNumber(text);
        setMessage(undefined);
    }, []);

    // CodeFieldProps

    const isCodeValid = useMemo(() => {
        return !!phoneNumberConfirmResultValue && codeValue.length >= CELL_COUNT;
    }, [codeValue, phoneNumberConfirmResultValue]);

    const isLoading = isProcessing;

    const validateCode = useCallback(async () => {
        try {
            setMessage(undefined);
            setIsProcessing(true);

            await phoneNumberConfirmResultValue?.confirm(codeValue);
            // signin will change the auth user
            // if user exists and is a worker the root navigations controller that will transition to the User stack
            // in all other cases, a hook will do the navigation to next screen
        } catch (_err) {
            setMessage({
                text: i18n.t("screens.auth.confirmation_code.wrong_code"),
                color: "red",
            });
            setIsProcessing(false);
        }
    }, [phoneNumberConfirmResultValue, codeValue, i18n]);

    useEffect(() => {
        // once user === null it means we have login to firebase (user has entered its code) and don't have an account yet
        if (firebaseUser && user === null) {
            navigation.replace("SignupSigninStack", {
                initialRouteName: "SelectRole",
                isSignup: !isLogin,
            });
        }
    }, [firebaseUser, user, navigation, isLogin]);

    useEffect(() => {
        if (isCodeValid) {
            void validateCode();
        }
    }, [isCodeValid, validateCode]);

    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const insets = useSafeAreaInsets();

    const backgroundLineRef = useRef<RandomLineFunctions>(null);
    const titleOpacity = useSharedValue(0);

    const opacityTitleStyles = useAnimatedStyle(() => ({
        opacity: titleOpacity.value,
    }));

    useEffect(() => {
        // Animate title fade-in on component mount
        titleOpacity.value = withTiming(1, {
            duration: 800,
        });
    }, [titleOpacity]);

    const loginTexts = useMemo(
        () => ({
            signup: {
                topRight: i18n.t("screens.auth.presentation_carousel.already_have_account"),
                title: i18n.t("screens.auth.welcome.start_new_adventure"),
                description: i18n.t("screens.auth.phone_number.description_web"),
                button: i18n.t("screens.auth.welcome.signup"),
            },
            login: {
                topRight: i18n.t("screens.auth.presentation_carousel.no_account"),
                title: i18n.t("screens.auth.welcome.connect"),
                description: i18n.t("screens.auth.phone_number.description"),
                button: i18n.t("login"),
            },
            invitation: {
                topRight: i18n.t("screens.auth.presentation_carousel.already_have_account"),
                title: i18n.t("screens.auth.welcome.invite", { invitedBy: invitedByLabel }),
                description: i18n.t("screens.auth.phone_number.description_web"),
                button: i18n.t("screens.auth.welcome.signup"),
            },
        }),
        [i18n, invitedByLabel],
    );

    const LIFT_LOGO_HEIGHT = 83.56;

    return (
        <>
            <View style={[styles.container]}>
                <RandomLine
                    ref={backgroundLineRef}
                    style={{ position: "absolute", left: -5, top: 0 }}
                    width={windowWidth * 1.2}
                    height={windowHeight + insets.top}
                    points={8}
                    color={[colors.quaternary, colors.secondary]}
                />
                <View style={[styles.leftContainer]}>
                    <LogoImage height={LIFT_LOGO_HEIGHT} width={114} />
                    <View style={styles.leftContentContainer}>
                        <MarkdownView
                            style={{
                                position: "absolute",
                                top: -65,
                                width: 400,
                                height: 64,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                            markdownStyle={{
                                textgroup: { textAlign: "center", color: colors.secondary },
                            }}
                            markdown={`## ${i18n.t("screens.auth.presentation_carousel.step_0.title")}`}
                        />
                        <View
                            style={{
                                width: LARGE_CAROUSEL_WIDTH,
                                height: LARGE_CAROUSEL_HEIGHT,
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                borderRadius: BUTTON_BORDER_RADIUS,
                                position: "relative",
                                backgroundColor: colors.background,
                            }}
                        >
                            <MaskedView
                                maskElement={
                                    <View
                                        style={{
                                            width: LARGE_CAROUSEL_WIDTH,
                                            height: LARGE_CAROUSEL_HEIGHT,
                                            borderRadius: BUTTON_BORDER_RADIUS,
                                            borderWidth: LARGE_CAROUSEL_BORDER_WIDTH,
                                        }}
                                    />
                                }
                            >
                                <LinearGradient
                                    start={{ x: 0.55, y: 0.55 }}
                                    end={{ x: 1, y: 0.4 }}
                                    locations={[0.5, 0.8]}
                                    colors={[colors.lighterGrey, colors.quaternary]}
                                    style={StyleSheet.absoluteFill}
                                />
                            </MaskedView>
                            <View
                                style={{
                                    height: LARGE_CAROUSEL_HEIGHT,
                                    width: LARGE_CAROUSEL_WIDTH,
                                    borderWidth: 10,
                                    borderColor: "transparent",
                                    borderRadius: BUTTON_BORDER_RADIUS,
                                    position: "absolute",
                                    overflow: "hidden",
                                }}
                            >
                                <AnimatedMarkdownView
                                    allowFontScaling={false}
                                    fontColor={colors.tertiary}
                                    markdownStyle={{
                                        heading1: { fontSize: 28, textAlign: "center" },
                                        textgroup: { textAlign: "center" },
                                    }}
                                    markdown={i18n.t(
                                        "screens.auth.presentation_carousel.step_1.title",
                                    )}
                                    style={[
                                        {
                                            flex: 1,
                                            paddingVertical: gridSize,
                                            paddingHorizontal: gridSize / 2,
                                            justifyContent: "center",
                                        },
                                        opacityTitleStyles,
                                    ]}
                                />
                            </View>
                        </View>
                    </View>
                    <View style={{ height: LIFT_LOGO_HEIGHT }} />
                </View>

                <View style={[styles.rightContainer]}>
                    <BlurView blurRadius={6} />
                    <LinearGradient
                        start={{ x: 0.55, y: 0.55 }}
                        end={{ x: 1, y: 0.4 }}
                        locations={[0.5, 0.8]}
                        colors={[colors.brightGrey, colors.quaternary]}
                        style={[StyleSheet.absoluteFill, { opacity: 0.8 }]}
                    />
                    <View style={styles.languageSelector}>
                        <LanguageSelector />
                    </View>
                    {!phoneNumberConfirmResultValue ? (
                        <VStack gap={1} style={styles.rightContentContainer}>
                            <VStack gap="1/3">
                                <TitleLargeText>{loginTexts[textType].title}</TitleLargeText>
                                <BodyMediumText>{loginTexts[textType].description}</BodyMediumText>
                            </VStack>
                            <VStack gap="2/3">
                                <PhoneField
                                    ref={phoneInputRef}
                                    value={phoneNumber}
                                    onPhoneNumberChanged={onPhoneNumberChanged}
                                    autoFocus
                                    placeholder={i18n.t(
                                        "screens.auth.phone_number.phone_placeholder",
                                    )}
                                    isValid={isPhoneNumberValid}
                                    style={styles.rightPhoneField}
                                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                    onSubmitEditing={validatePhoneNumber}
                                />
                                <Button
                                    label={loginTexts[textType].button}
                                    size="large"
                                    onPress={validatePhoneNumber}
                                    processing={isProcessing}
                                />
                            </VStack>
                        </VStack>
                    ) : (
                        <VStack gap={1} style={styles.rightContentContainer}>
                            <VStack gap="1/3" alignment="center">
                                <TitleLargeText>
                                    {i18n.t("screens.auth.confirmation_code.title")}
                                </TitleLargeText>
                                <BodyMediumText>
                                    {i18n.t("screens.auth.confirmation_code.description", {
                                        phoneNumber,
                                    })}
                                </BodyMediumText>
                            </VStack>
                            <VStack gap="2/3">
                                <CodeField
                                    value={codeValue}
                                    setValue={setCodeValue}
                                    cellCount={6}
                                    autoFocus
                                    noBorders
                                    cellStyle={
                                        isLoading ? { backgroundColor: colors.backdrop } : {}
                                    }
                                />
                                <Loading
                                    color={colors.onPrimary}
                                    style={{ opacity: isLoading ? 1 : 0 }}
                                />
                            </VStack>
                        </VStack>
                    )}
                    <Text
                        style={[
                            commonStyle.screenErrorMessage,
                            { color: message?.color, opacity: message ? 1 : 0 },
                        ]}
                    >
                        {message?.text || " "}
                    </Text>
                </View>
                {!isInitialLogin && (
                    <VStack gap="2/3" style={styles.ourPartnersContainer}>
                        <BodySmallText>
                            {i18n.t("screens.auth.presentation_carousel.our_partners")}
                        </BodySmallText>
                        <View style={styles.ourPartnersLogos}>
                            <AutoSizeImage
                                style={{ transform: [{ translateY: -3.5 }] }}
                                source={require("../../../../assets/images/centre_patronal.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                            />
                            <AutoSizeImage
                                source={require("../../../../assets/images/suva.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                                scale={0.6}
                            />
                            <AutoSizeImage
                                source={require("../../../../assets/images/gastrosocial.png")}
                                fixedHeight={PARTNER_LOGO_HEIGHT}
                                scale={0.7}
                            />
                        </View>
                    </VStack>
                )}
                <View style={styles.qrLineContainer}>
                    <View style={styles.qrCodeWrapper}>
                        <QRCode
                            size={53}
                            bgColor="transparent"
                            value="https://app.liftapp.ch/invite"
                        />
                    </View>
                    <MarkdownView
                        style={{ flex: 1, transform: [{ translateY: 4 }] }}
                        markdownStyle={{ paragraph: styles.paragraph, strong: styles.strong }}
                        markdown={i18n.t("screens.auth.welcome.scan_qr_code")}
                    />
                </View>
                {!phoneNumberConfirmResultValue && (
                    <Pressable
                        style={styles.topRightPressable}
                        onPress={() => {
                            if (isInitialLogin) {
                                navigation.replace("Welcome");
                            } else {
                                if (textType === "invitation" || textType === "signup") {
                                    setIsLogin(true);
                                } else {
                                    setIsLogin(false);
                                }
                            }
                        }}
                    >
                        <BodySmallText style={{ color: colors.secondary }}>
                            {loginTexts[textType].topRight}
                        </BodySmallText>
                    </Pressable>
                )}
                {Platform.OS === "web" && <div id={CAPTCHA_ID} />}
            </View>
        </>
    );
};

const useStyles = () => {
    const { gridSize, marginSize, colors, fonts } = useTheme();

    return StyleSheet.create({
        container: {
            flexDirection: "row",
            flex: 1,
            overflow: "hidden",
        },
        leftContainer: {
            width: "50%",
            height: "100%",
            minHeight: 800,
            position: "absolute",
            alignItems: "center",
            justifyContent: "space-around",
            paddingHorizontal: gridSize,
        },
        leftContentContainer: {
            alignItems: "center",
            justifyContent: "center",
        },
        ourPartnersContainer: {
            position: "absolute",
            bottom: 32,
            left: 32,
            flexDirection: "column",
            width: "100%",
        },
        ourPartnersLogos: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: 510,
            paddingHorizontal: gridSize / 2,
        },
        qrLineContainer: {
            position: "absolute",
            left: "52%",
            bottom: 32,
            flexDirection: "row",
            alignItems: "flex-end",
        },
        qrCodeWrapper: {
            marginRight: marginSize / 2,
        },
        paragraph: {
            ...fonts.bodySmall,
            fontSize: 13,
        },
        strong: {
            ...fonts.bold,
            fontSize: 13,
        },
        bodySmallText: {
            ...fonts.bodySmall,
            color: colors.primary,
        },
        topRightPressable: {
            position: "absolute",
            top: 30,
            right: 24,
        },
        rightContainer: {
            padding: gridSize,
            justifyContent: "center",
            alignItems: "center",
            marginLeft: "50%",
            width: "50%",
        },
        rightContentContainer: {
            maxWidth: 500,
        },
        rightPhoneField: {
            backgroundColor: colors.background,
            borderRadius: 100,
            color: colors.onPrimary,
            borderColor: "transparent",
        },
        languageSelector: {
            position: "absolute",
            top: (gridSize * 2) / 3,
            left: (gridSize * 2) / 3,
        },
    });
};
